import {useTranslation} from '@aboutyou/i18n';
import {styled as linariaStyled} from '@linaria/react';
import React, {useEffect, useState} from 'react';
import sc from 'styled-components';
import {COLORS} from '../../../constants/colors';
import {useInView} from '../../../hooks/useInView/useInView';
import {UnstyledButton} from '../../atoms/Buttons/UnstyledButton';
import {Icon} from '../../atoms/Icon/Icon';
import {GenericIconName} from '../../atoms/Icon/IconName';
import {ProductImage} from '../ProductImage/ProductImage';
import {
  Image,
  ProductTileImageProps,
} from '../ProductTileImage/ProductTileImage';
import {SliderBullets} from '../SliderBullets/SliderBullets';
import {useProductTileImageSlider} from './utils/useProductTileImageSlider';

interface ProductTileImageSliderProps
  extends Omit<ProductTileImageProps, 'primaryImage' | 'secondaryImage'> {
  imageGallery: Image[];
  trackGallerySwipe?: VoidFunction;
  productTileGalleryMode?: 'hover' | 'always';
}

export const ProductTileImageSlider: React.FC<ProductTileImageSliderProps> = ({
  productImageContent = [],
  columns,
  breakpoints,
  importance,
  imageGallery,
  trackGallerySwipe,
  productTileGalleryMode,
  enableImageGalleryAnimation,
  ...rest
}) => {
  const [renderOnlyFirstImage, setRenderOnlyFirstImage] = useState(true);
  const [shouldAnimate, setShouldAnimate] = useState(false);
  const {
    sliderRef,
    currentSlide,
    previousSlide,
    handleOnScroll,
    handleScrollTo,
  } = useProductTileImageSlider({
    renderOnlyFirstImage,
    setRenderOnlyFirstImage,
  });

  const {inView, ref} = useInView<HTMLDivElement>();

  useEffect(
    () => {
      (currentSlide !== 0 || previousSlide !== undefined) &&
        trackGallerySwipe?.();
    },
    // `previousSlide` not part of the dependency array to avoid tracking gallery swipe twice
    // `trackGallerySwipe` not part of the dependency array to avoid incorrect tracking, e.g. when the mini ADP is opened
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentSlide],
  );

  // for performance reasons we render only the first slider image, all the other images are shallow rendered
  const renderProductImages = (images: Image[], shallowRender: boolean) => {
    return images.map((image, index) => {
      const isCloseToViewport =
        currentSlide - 1 <= index && currentSlide + 1 >= index;
      return (
        <li key={index}>
          {shallowRender ? (
            <ShallowProductImage data-testid="shallowProductImage" />
          ) : (
            <StyledProductImage
              columns={columns}
              breakpoints={breakpoints}
              loading={isCloseToViewport ? 'eager' : 'lazy'}
              increasePadding
              {...image}
            />
          )}
        </li>
      );
    });
  };

  const firstImage = imageGallery[0]!;

  const onMouseEnter = () => {
    if (productTileGalleryMode === 'hover') {
      setRenderOnlyFirstImage(false);
    }
  };

  const goToNextSlide = () => {
    const nextSlide = currentSlide + 1;
    handleScrollTo(nextSlide);
  };

  const goToPreviousSlide = () => {
    const nextSlide = currentSlide - 1;
    handleScrollTo(nextSlide);
  };

  const onScroll = (e: React.UIEvent<HTMLUListElement>) => {
    setShouldAnimate(false);
    handleOnScroll(e);
  };

  return (
    <div onMouseEnter={onMouseEnter} {...rest} ref={ref}>
      <ProductImageSliderWrapper>
        <ProductImageSlider
          data-testid="productTileImageSlider"
          ref={sliderRef}
          onScroll={onScroll}
          data-swipe-animation={enableImageGalleryAnimation && shouldAnimate}
          data-scroll={inView}
          tabIndex={0}
        >
          {/* the first image is always rendered */}
          <li>
            <StyledProductImage
              alt={firstImage.alt}
              columns={columns}
              breakpoints={breakpoints}
              importance={importance}
              loading={importance === 'high' ? 'eager' : undefined}
              padded={firstImage.padded}
              src={firstImage.src}
              onLoad={() => setShouldAnimate(true)}
              increasePadding
            />
          </li>

          {renderProductImages(
            imageGallery.slice(1),
            renderOnlyFirstImage && !enableImageGalleryAnimation,
          )}
        </ProductImageSlider>
        <ProductTileImageContentWrapper>
          {productImageContent}
        </ProductTileImageContentWrapper>
        {productTileGalleryMode === 'hover' && (
          <>
            <Navigation
              dataTestid="productTileImageSliderNavigation"
              goToNextSlide={goToNextSlide}
              goToPreviousSlide={goToPreviousSlide}
              currentSlide={currentSlide}
              slidesCount={imageGallery.length}
            />
            <StyledSliderBullets
              data-testid="productTileImageSliderBullets"
              slidesNumber={imageGallery.length}
              currentSlide={currentSlide}
              navBtnsShown={false}
              navOverSlider={false}
            />
          </>
        )}
      </ProductImageSliderWrapper>
    </div>
  );
};

const Navigation = (props: {
  goToNextSlide: VoidFunction;
  goToPreviousSlide: VoidFunction;
  currentSlide: number;
  slidesCount: number;
  dataTestid?: string;
}) => {
  const {goToNextSlide, goToPreviousSlide, currentSlide, slidesCount} = props;
  const {t} = useTranslation();

  const firstSlide = currentSlide === 0;

  const lastSlide = currentSlide === slidesCount - 1;

  return (
    <NavigationWrapper
      data-first-slide={firstSlide}
      data-testid={props.dataTestid}
    >
      <GoToPreviousSlide
        onClick={e => {
          e.stopPropagation();
          e.preventDefault();
          goToPreviousSlide();
        }}
        data-hidden={firstSlide}
        aria-label={t('Go to the previous product image')}
        tabIndex={-1}
      >
        <Icon asset={GenericIconName.ArrowLeft} width={22} height={22} />
      </GoToPreviousSlide>
      <GoToNextSlide
        onClick={e => {
          e.stopPropagation();
          e.preventDefault();
          goToNextSlide();
        }}
        data-hidden={lastSlide}
        aria-label={t('Go to the next product image')}
        tabIndex={-1}
      >
        <Icon asset={GenericIconName.ArrowRight} width={22} height={22} />
      </GoToNextSlide>
    </NavigationWrapper>
  );
};

const ProductTileImageContentWrapper = linariaStyled.div`
  transition: transform 250ms ease-in-out, opacity 250ms ease-in-out;
`;

export const GoToSlideWrapper = linariaStyled(UnstyledButton)`
  transition: transform 250ms ease-in-out, opacity 250ms ease-in-out;
  padding: 0.6rem 0.4rem;
  background-color: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(10px);
  opacity: 1;
  &[data-hidden='true'] {
    opacity: 0;
  }
`;

const GoToNextSlide = sc(GoToSlideWrapper)`
  transform: translateX(40px);
  border-radius: 20px 0 0 20px;
  cursor: pointer;
`;
const GoToPreviousSlide = sc(GoToSlideWrapper)`
  transform: translateX(-40px);
  border-radius: 0 20px 20px 0;
  cursor: pointer;
`;

const NavigationWrapper = sc.div`
  position: absolute;
  display: flex;
  justify-content: space-between;
  width: 100%;
  height: 100%;
  top: 0;
  align-items: center;
  z-index: 1;
`;

export const StyledSliderBullets = linariaStyled(SliderBullets)`
  position: absolute;
  transition: opacity 300ms ease-in, transform 250ms ease-in;
  transition-delay: 60ms;
  transform: translateY(-4px);
`;

const ProductImageSliderWrapper = sc.div`
  position: relative;
`;

const StyledProductImage = sc(ProductImage)``;

const ProductImageSlider = sc.ul`
  @keyframes animateSwipe {
    0% {
      transform: translateX(0);
    }
    50% {
      transform: translateX(-150px);
    }
    100% {
      transform: translateX(0);
    }
  }
  display: flex;
  scroll-snap-type: x mandatory;
  scroll-snap-stop: always;
  height: fit-content;
  margin: 0;
  padding: 0;

  &[data-scroll='true'] {
    overflow: auto;
  }

  -ms-overflow-style: none;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }

  & > li {
    flex: 1 0 100%;
    scroll-snap-align: center;

    list-style: none;
  }

  &[data-swipe-animation='true'] {
    ${StyledProductImage} {
      animation: animateSwipe 1s ease-in-out;
      animation-delay: 2s;
    }
  }
`;

const ShallowProductImage = sc.div`
  background-color: ${COLORS.ProductImageBackground};
`;
